import { Card } from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import useApi from "../../hooks/useApi";
import { API_METHODS, BLANK_VALUE } from "../../constants";
import { useMediaQuery } from "react-responsive";
import { theme } from "../../assets/theme";
import heroTnC from "../../assets/img/hero-tnc.png";
import Footer from "./HomePage/Footer";
import Breadcrumbs from "../../components/common/Breadcrumbs";

function Privacy() {
  const [getSeoDataResponse, getSeoDataRequest] = useApi(
    `/website/static-seo?page_identifier=Terms-Policy`,
    null,
    API_METHODS.GET
  );

  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    getSeoDataRequest();
  }, []);

  useEffect(() => {
    if (getSeoDataResponse.data && !getSeoDataResponse.isLoading) {
      const data =
        getSeoDataResponse.data !== BLANK_VALUE
          ? getSeoDataResponse.data
          : null;
      setSeoData(data);
    }
  }, [getSeoDataResponse]);

  // SEO Helper Functions
  const generateOrganizationSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Cenvance Innovations Ltd",
      url: "https://cenvance.com",
      logo: "https://cenvance.com/logo.png",
      description:
        "We provide high-quality solutions that empower research laboratories, hospitals, and diagnostic centers worldwide.",
    };
  };

  const generateWebPageSchema = () => {
    if (!seoData) return null;

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seoData.page_identifier || "Terms and Policies",
      description: seoData.meta_description,
      url: "https://cenvance.com/terms-and-policies",
      inLanguage: "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Cenvance Innovations Ltd",
        url: "https://cenvance.com",
      },
      keywords: seoData.meta_keywords,
    };
  };

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>
          {seoData?.meta_title ||
            "Terms and Policies - Cenvance Innovations Ltd"}
        </title>
        <meta
          name="description"
          content={
            seoData?.meta_description ||
            "Terms of service, privacy policy, return policy, and warranty information for Cenvance Innovations Ltd."
          }
        />
        {seoData?.meta_keywords && (
          <meta name="keywords" content={seoData.meta_keywords} />
        )}
        <meta name="robots" content={seoData?.meta_robots || "index, follow"} />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={
            seoData?.canonical_url || "https://cenvance.com/terms-and-policies"
          }
        />

        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenvance Innovations Ltd" />
        <meta
          property="og:url"
          content={
            seoData?.canonical_url || "https://cenvance.com/terms-and-policies"
          }
        />
        <meta
          property="og:title"
          content={
            seoData?.og_title ||
            seoData?.meta_title ||
            "Terms and Policies - Cenvance Innovations Ltd"
          }
        />
        <meta
          property="og:description"
          content={
            seoData?.og_description ||
            seoData?.meta_description ||
            "Terms of service and privacy policy"
          }
        />
        {seoData?.og_image && (
          <meta property="og:image" content={seoData.og_image} />
        )}

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            seoData?.twitter_title ||
            seoData?.meta_title ||
            "Terms and Policies - Cenvance Innovations Ltd"
          }
        />
        <meta
          name="twitter:description"
          content={
            seoData?.twitter_description ||
            seoData?.meta_description ||
            "Terms of service and privacy policy"
          }
        />
        {seoData?.twitter_image && (
          <meta name="twitter:image" content={seoData.twitter_image} />
        )}

        {/* Structured Data Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationSchema())}
        </script>

        {seoData && (
          <script type="application/ld+json">
            {JSON.stringify(generateWebPageSchema())}
          </script>
        )}

        {/* Additional Schema if exists */}
        {seoData?.additional_schema && (
          <script type="application/ld+json">
            {typeof seoData.additional_schema === "string"
              ? seoData.additional_schema
              : JSON.stringify(seoData.additional_schema)}
          </script>
        )}
      </Helmet>
      <div>
        <img
          alt="Hero"
          src={heroTnC}
          style={{ height: "auto", width: "100%" }}
        />
      </div>
      <Breadcrumbs
        items={[
          {
            title: "Terms and Policies",
          },
        ]}
      />
      <Card
        style={{
          color: theme.colors.darkGray,
          marginBottom: 30,
          marginTop: 30,
          borderRadius: 50,
          width: isTabletOrMobile ? "90%" : "80%",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <div className="terms-and-policies">
          <div className="company-info">
            <h1
              className="company-title"
              style={{
                color: theme.colors.black,
                textAlign: "center",
                marginBottom: 30,
              }}
            >
              Cenvance Innovation Limited
            </h1>
            <p className="company-description" style={{ textAlign: "center" }}>
              Welcome to Cenvance Innovation Limited Company. We value your
              trust and aim to provide full clarity on how we operate. This page
              outlines our terms of service, privacy practices, return policies,
              warranty guidelines, and overall business ethics. By using our
              website or purchasing our products/services, you agree to follow
              these terms.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              1. GENERAL TERMS OF SERVICE
            </h3>
            <p className="section-text">
              At Cenvance Innovation Limited, we supply advanced laboratory and
              medical instruments and technical services to research
              institutions, universities, and industries. All orders are subject
              to review and confirmation. We reserve the right to modify product
              details, pricing, or availability without prior notice. Once your
              order is confirmed in writing (email), the agreement becomes
              valid.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              2. Pricing & Payment Policy
            </h3>
            <p className="section-text">
              All prices are quoted in USD, EUR, GBP, INR, and AED, exclusive of
              applicable taxes.
              <br /> Delivery, packaging, and handling charges may be added to
              the final invoice as required.
            </p>
            <p className="section-text">Accepted payment methods include:</p>
            <ul className="section-text">
              <li>International Wire Transfer (SWIFT)</li>
              <li>TT (Telegraphic Transfer)</li>
              <li>PayPal (for select transactions and low-value payments)</li>
              <li>Wise (formerly TransferWise)</li>
              <li>Western Union (if pre-approved)</li>
              <li>
                LC (Letter of Credit) - for high-value orders, subject to
                approval
              </li>
            </ul>
            <p className="section-text">
              All payments must be made in full before dispatch unless credit
              terms are pre-approved in writing.
            </p>
          </div>

          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              3. Shipping & Delivery{" "}
            </h3>
            <p className="section-text">
              Shipping is arranged to the customer's provided address through{" "}
              <b style={{ color: theme.colors.black }}>
                reliable and verified courier or freight partners
              </b>
              .{" "}
              <b style={{ color: theme.colors.black }}>
                Estimated delivery timelines
              </b>{" "}
              may vary depending on the destination, product availability, and
              customs clearance procedures.
            </p>
            <p className="section-text">
              <b style={{ color: theme.colors.black }}>
                Ownership and risk transfer
              </b>{" "}
              to the customer once the goods are handed over to the logistics
              provider. We are{" "}
              <b style={{ color: theme.colors.black }}>not liable for delays</b>
              arising from external factors beyond our control, such as:
            </p>
            <ul className="section-text">
              <li>Adverse weather conditions</li>
              <li>Customs inspections or clearance delays</li>
              <li>Labor strikes or operational disruptions</li>
              <li>Carrier or transportation failures</li>
            </ul>
            <p className="section-text">
              Tracking details will be shared once the shipment is dispatched.
              Customers are advised to monitor the shipment and report any
              issues promptly.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              4. Return & Replacement Policy{" "}
            </h3>
            <p className="section-text">
              <b style={{ color: theme.colors.black }}>
                Returns are not accepted
              </b>{" "}
              unless there is a
              <b style={{ color: theme.colors.black }}>
                {" "}
                genuine and verifiable issue{" "}
              </b>{" "}
              with the product, such as manufacturing defects or damage during
              transit.
            </p>
            <p className="section-text">
              All such cases will be subject to a
              <b style={{ color: theme.colors.black }}>
                {" "}
                thorough inspection and technical evaluation
              </b>{" "}
              before any return or replacement is approved.
            </p>
            <p className="section-text">
              Products must be reported as defective or damaged within{" "}
              <b style={{ color: theme.colors.black }}>3 days of delivery, </b>{" "}
              along with supporting evidence (photos, videos, and unboxing if
              possible).
            </p>
            <p className="section-text">
              <b style={{ color: theme.colors.black }}>
                {" "}
                Customized, special-order, or made-to-order items{" "}
              </b>{" "}
              are strictly non-returnable and non-refundable. If the fault is
              confirmed to be on our side (manufacturing or shipment error),
              Cenvance Innovations Ltd. will bear the return freight and
              replacement cost.
            </p>
            <p className="section-text">
              In all other cases, the{" "}
              <b style={{ color: theme.colors.black }}>
                buyer will be responsible for return logistics and associated
                charges
              </b>
              .
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              5.Warranty Policy
            </h3>
            <p className="section-text">
              Most equipment is backed by a standard{" "}
              <b style={{ color: theme.colors.black }}>
                12-month limited warranty
              </b>{" "}
              from the delivery date.
            </p>
            <p className="section-text">
              The warranty covers manufacturing defects but excludes damage due
              to mishandling, unauthorized repairs, or improper usage.
            </p>
            <p className="section-text">
              Warranty claims must be raised within
              <b style={{ color: theme.colors.black }}> 7 days</b> of
              discovering an issue.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              6.Liability Policy
            </h3>
            <p className="section-text">
              Our liability is limited to the product's invoice value.
            </p>
            <p className="section-text">
              We are not liable for indirect or consequential losses, including
              downtime or third-party claims.
            </p>
            <p className="section-text">
              We do not take responsibility for the use of equipment beyond its
              stated specifications or operational environment.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              7.Privacy Policy
            </h3>
            <p className="section-text">
              We collect personal information such as{" "}
              <b style={{ color: theme.colors.black }}>
                name, address, contact details, and payment info
              </b>{" "}
              solely for order processing and service delivery.
            </p>
            <p className="section-text">
              We do not sell, share, or disclose your data to third parties,
              except when legally required.
            </p>
            <p className="section-text">
              All data is stored securely and used only for internal operations.
            </p>
            <p className="section-text">
              By interacting with our website or services, you consent to this
              policy.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              8.Intellectual Property
            </h3>
            <p className="section-text">
              All logos, content, images, and documents used on this website are
              the property of Cenvance Innovation Limited and may not be reused
              without permission.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              9.Force Majeure
            </h3>
            <p className="section-text">
              We shall not be held liable for failure to deliver or delay in
              performance due to events beyond our reasonable control including
              but not limited to natural disasters, political unrest, or
              supplier issues.
            </p>
          </div>
          <div className="section">
            <h3
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              10.Governing Law & Jurisdiction
            </h3>
            <p className="section-text">
              These terms and policies are governed by the laws of the{" "}
              <b style={{ color: theme.colors.black }}> United Kingdom.</b>
            </p>
            <p className="section-text">
              Any disputes arising out of or in connection with our products,
              services, or agreements shall fall under the
              <b style={{ color: theme.colors.black }}>
                {" "}
                exclusive jurisdiction of the courts of England and Wales.
              </b>
            </p>
          </div>
          <div className="section">
            <h2
              className="section-title"
              style={{ color: theme.colors.black, textTransform: "uppercase" }}
            >
              {" "}
              CONTACT US
            </h2>
            <p className="section-text">
              If you have any questions regarding these Terms, please contact us
              at:
            </p>
            <p className="section-text">Cenvance Innovation Limited</p>
            <p className="section-text">
              Address:85 Great Portland Street First Floor London W1W 7LT
            </p>
            <p className="section-text">Phone: +44 20 3318 3727</p>
            <p className="section-text">Email: info@cenvance.com</p>
          </div>
        </div>
      </Card>
      <Footer isTabletOrMobile={isTabletOrMobile} />
    </>
  );
}

export default Privacy;
